import React, { useState } from 'react';
import {
  Server, Key, BarChart3, FileText, Activity, Eye,
  ExternalLink, CheckCircle, AlertCircle, Clock,
  Shield, Database, Network, Cpu, HardDrive, RefreshCw
} from 'lucide-react';
import { theme } from '../../styles/theme';
import config from '../../config';

interface SystemAdminProps {
  darkMode: boolean;
}

const SystemAdmin: React.FC<SystemAdminProps> = ({ darkMode }) => {
  const [systemStatus] = useState('healthy');

  // Define all possible admin tools with config URLs
  const allAdminTools = [
    {
      id: 'keycloak',
      name: 'Keycloak',
      description: 'Identity and Access Management',
      icon: Key,
      url: config.keycloakAdminUrl,
      status: 'healthy',
      metrics: {
        users: '1,247',
        sessions: '423',
        realms: '3',
      },
      color: theme.colors.status.success,
    },
    {
      id: 'grafana',
      name: 'Grafana',
      description: 'Metrics Visualization & Dashboards',
      icon: BarChart3,
      url: config.grafanaUrl,
      status: 'healthy',
      metrics: {
        dashboards: '12',
        alerts: '37',
        datasources: '5',
      },
      color: theme.colors.primary.main,
    },
    {
      id: 'kibana',
      name: 'Kibana',
      description: 'Log Analysis & Search',
      icon: FileText,
      url: config.kibanaUrl,
      status: 'healthy',
      metrics: {
        indices: '8',
        documents: '2.3M',
        searches: '1.2K/min',
      },
      color: theme.colors.secondary.main,
    },
    {
      id: 'jaeger',
      name: 'Jaeger',
      description: 'Distributed Tracing',
      icon: Activity,
      url: config.jaegerUrl,
      status: 'healthy',
      metrics: {
        services: '15',
        traces: '45K',
        spans: '2.1M',
      },
      color: '#60A5FA',
    },
    {
      id: 'prometheus',
      name: 'Prometheus',
      description: 'Metrics Collection & Alerting',
      icon: Eye,
      url: config.prometheusUrl,
      status: 'warning',
      metrics: {
        targets: '23',
        rules: '156',
        alerts: '3 active',
      },
      color: '#F97316',
    },
    {
      id: 'kafka-ui',
      name: 'Kafka UI',
      description: 'Message Queue Management',
      icon: Network,
      url: config.kafkaUiUrl,
      status: 'healthy',
      metrics: {
        topics: '3',
        brokers: '1',
        partitions: '5',
      },
      color: '#10B981',
    },
  ];

  // Filter out tools where URL is not configured
  const adminTools = allAdminTools.filter(tool => tool.url !== null && tool.url !== '');

  const systemMetrics = [
    { name: 'CPU Usage', value: '42%', icon: Cpu, status: 'healthy' },
    { name: 'Memory', value: '6.8 GB / 16 GB', icon: HardDrive, status: 'healthy' },
    { name: 'Network I/O', value: '124 MB/s', icon: Network, status: 'healthy' },
    { name: 'Database Connections', value: '87 / 100', icon: Database, status: 'warning' },
  ];

  const recentAlerts = [
    { id: 1, level: 'warning', message: 'High memory usage on user-service', time: '5 minutes ago' },
    { id: 2, level: 'info', message: 'Scheduled backup completed successfully', time: '1 hour ago' },
    { id: 3, level: 'error', message: 'Failed to connect to Redis cache', time: '2 hours ago', resolved: true },
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5" style={{ color: theme.colors.status.success }} />;
      case 'warning':
        return <AlertCircle className="w-5 h-5" style={{ color: theme.colors.status.warning }} />;
      case 'error':
        return <AlertCircle className="w-5 h-5" style={{ color: theme.colors.status.error }} />;
      default:
        return <Clock className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ 
            color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
          }}>
            <Server className="w-6 h-6" style={{ color: theme.colors.primary.main }} />
            System Administration
          </h1>
          <p className="text-sm mt-1" style={{ color: theme.colors.text.secondary }}>
            Monitor and manage system components and infrastructure
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2 px-4 py-2 rounded-lg" style={{
            backgroundColor: systemStatus === 'healthy' 
              ? theme.colors.status.success + '20'
              : theme.colors.status.warning + '20',
          }}>
            {getStatusIcon(systemStatus)}
            <span className="text-sm font-medium" style={{
              color: systemStatus === 'healthy' 
                ? theme.colors.status.success
                : theme.colors.status.warning,
            }}>
              System {systemStatus === 'healthy' ? 'Healthy' : 'Degraded'}
            </span>
          </div>
          <button className="p-2 rounded-lg transition-colors hover:bg-black hover:bg-opacity-5">
            <RefreshCw className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />
          </button>
        </div>
      </div>

      {/* System Metrics */}
      <div className="grid grid-cols-4 gap-4">
        {systemMetrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div key={metric.name} className="p-4 rounded-xl" style={{
              backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
              boxShadow: theme.widget.boxShadow,
            }}>
              <div className="flex items-center justify-between mb-2">
                <Icon className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />
                <div className="w-2 h-2 rounded-full" style={{
                  backgroundColor: metric.status === 'healthy' 
                    ? theme.colors.status.success
                    : theme.colors.status.warning,
                }}></div>
              </div>
              <p className="text-sm" style={{ color: theme.colors.text.secondary }}>
                {metric.name}
              </p>
              <p className="text-lg font-semibold mt-1" style={{ 
                color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
              }}>
                {metric.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Admin Tools Grid */}
      <div>
        <h2 className="text-lg font-semibold mb-4" style={{ 
          color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
        }}>
          Administration Tools
        </h2>
        <div className="grid grid-cols-3 gap-4">
          {adminTools.map((tool) => {
            const Icon = tool.icon;
            return (
              <div key={tool.id} className="rounded-xl overflow-hidden" style={{
                backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
                boxShadow: theme.widget.boxShadow,
              }}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-lg" style={{
                      backgroundColor: tool.color + '20',
                    }}>
                      <Icon className="w-6 h-6" style={{ color: tool.color }} />
                    </div>
                    {getStatusIcon(tool.status)}
                  </div>
                  
                  <h3 className="font-semibold text-lg mb-1" style={{ 
                    color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                  }}>
                    {tool.name}
                  </h3>
                  <p className="text-sm mb-4" style={{ color: theme.colors.text.secondary }}>
                    {tool.description}
                  </p>

                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {Object.entries(tool.metrics).map(([key, value]) => (
                      <div key={key}>
                        <p className="text-xs" style={{ color: theme.colors.text.secondary }}>
                          {key.charAt(0).toUpperCase() + key.slice(1)}
                        </p>
                        <p className="font-semibold" style={{ 
                          color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                        }}>
                          {value}
                        </p>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => window.open(tool.url!, '_blank')}
                    className="w-full px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                    style={{
                      backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
                      color: tool.color,
                      border: `1px solid ${tool.color}40`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = tool.color + '20';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = darkMode 
                        ? theme.colors.background.dark 
                        : theme.colors.background.default;
                    }}
                  >
                    <span>Open {tool.name}</span>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Alerts */}
      <div className="rounded-xl p-6" style={{
        backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
        boxShadow: theme.widget.boxShadow,
      }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ 
            color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
          }}>
            Recent System Alerts
          </h2>
          <button className="text-sm" style={{ color: theme.colors.primary.main }}>
            View All
          </button>
        </div>

        <div className="space-y-3">
          {recentAlerts.map((alert) => (
            <div key={alert.id} className="flex items-start space-x-3 p-3 rounded-lg" style={{
              backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
              opacity: alert.resolved ? 0.6 : 1,
            }}>
              <div className="mt-1">
                {alert.level === 'error' ? (
                  <AlertCircle className="w-4 h-4" style={{ color: theme.colors.status.error }} />
                ) : alert.level === 'warning' ? (
                  <AlertCircle className="w-4 h-4" style={{ color: theme.colors.status.warning }} />
                ) : (
                  <CheckCircle className="w-4 h-4" style={{ color: theme.colors.status.info }} />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm" style={{ 
                  color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                }}>
                  {alert.message}
                  {alert.resolved && (
                    <span className="ml-2 text-xs px-2 py-1 rounded" style={{
                      backgroundColor: theme.colors.status.success + '20',
                      color: theme.colors.status.success,
                    }}>
                      Resolved
                    </span>
                  )}
                </p>
                <p className="text-xs mt-1" style={{ color: theme.colors.text.secondary }}>
                  {alert.time}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Security Status */}
      <div className="rounded-xl p-6" style={{
        backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
        boxShadow: theme.widget.boxShadow,
      }}>
        <div className="flex items-center space-x-3 mb-4">
          <Shield className="w-5 h-5" style={{ color: theme.colors.status.success }} />
          <h2 className="text-lg font-semibold" style={{ 
            color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
          }}>
            Security Status
          </h2>
        </div>
        
        <div className="grid grid-cols-4 gap-4">
          <div>
            <p className="text-sm" style={{ color: theme.colors.text.secondary }}>
              SSL Certificates
            </p>
            <p className="font-semibold mt-1" style={{ 
              color: theme.colors.status.success 
            }}>
              Valid (287 days)
            </p>
          </div>
          <div>
            <p className="text-sm" style={{ color: theme.colors.text.secondary }}>
              Last Security Scan
            </p>
            <p className="font-semibold mt-1" style={{ 
              color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
            }}>
              2 hours ago
            </p>
          </div>
          <div>
            <p className="text-sm" style={{ color: theme.colors.text.secondary }}>
              Active Sessions
            </p>
            <p className="font-semibold mt-1" style={{ 
              color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
            }}>
              423
            </p>
          </div>
          <div>
            <p className="text-sm" style={{ color: theme.colors.text.secondary }}>
              Failed Login Attempts
            </p>
            <p className="font-semibold mt-1" style={{ 
              color: theme.colors.status.warning 
            }}>
              12 (last 24h)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemAdmin;